import {
    Approved,
    DecisionDataStore,
    Declined,
    LoanSignContainer,
    SignatureDataStore,
    UnderReview,
} from '@servicetitan/financing-decision-shared';
import { ApplicationWorkflow } from './ApplicationWorkflow';
import { SecondLookApplicationWorkflow } from './SecondLookApplicationWorkflow';
import { DecisionWorkflow } from './DecisionWorkflow';
import { SubmitLoan } from './SubmitLoan';
import {
    WorkflowStage,
    WorkflowStore,
    useProviderConfig,
    SetupDataStore,
} from '@servicetitan/financing-workflow-shared';
import { SubmitApplicationDecision } from '@servicetitan/financing-api-client';
import { ErrorBoundary } from '@servicetitan/financing-utils-shared';
import { SecondLookSubmitLoanWorkflow } from './SecondLookSubmitLoanWorkflow';
import { SecondLookDecisionWorkflow } from './SecondLookDecisionWorkflow';
import { WorkflowLayoutsStore } from '../layout';
import { ExternalApplicationWorkflowStore } from '../external-application';
import { ResetWorkflow } from './ResetWorkflow';
import { SelectPlanStep, SelectSecondLookStep, CustomerAuthStep } from '../steps';
import { ReviewingWorkflow } from './ReviewingWorkflow';
import { WellsFargoReviewingWorkflow } from './WellsFargoReviewingWorkflow';
import { ApplicationWithCoApplicationWorkflow } from './ApplicationWithCoApplicationWorkflow';
import { TransitionToHardApp } from './TransitionToHardApp';
import { UpdateOfferWorkflow } from './UpdateOfferWorkflow';

interface WorkflowRendererProps {
    workflowStage: WorkflowStage;
    canChangeProvider?: boolean;
}

export const WorkflowRenderer = ({ workflowStage, canChangeProvider }: WorkflowRendererProps) => {
    const { decisionData } = DecisionDataStore.useStore();
    const { signatureData } = SignatureDataStore.useStore();
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { DefaultLayout, FinalStepLayout } = WorkflowLayoutsStore.useStore();
    const { ExternalApplicationWorkflow } = ExternalApplicationWorkflowStore.useStore();
    const { hasMailingAddressStep, useCustomerAuthFlow } = useProviderConfig();
    const { setupData } = SetupDataStore.useStore();
    const onPlanSelected = () => {
        if (useCustomerAuthFlow) {
            setWorkflowStage('customer-auth');
        } else if (!hasMailingAddressStep) {
            // Providers without separate mailing address step use the compact workflow
            // with job details inline (e.g., ServiceFinance with Turns, GoodLeap, GreenSky)
            setWorkflowStage('application-with-co-application');
        } else {
            setWorkflowStage('application');
        }
    };

    switch (workflowStage) {
        case 'external-application':
            return <ExternalApplicationWorkflow />;
        case 'transition-to-hard-app':
            return <TransitionToHardApp onCompleted={() => setWorkflowStage('plan-selection')} />;
        case 'plan-selection':
            return (
                <DefaultLayout>
                    <SelectPlanStep
                        canChangeProvider={canChangeProvider}
                        onPlanSelected={onPlanSelected}
                    />
                </DefaultLayout>
            );
        case 'customer-auth':
            return (
                <DefaultLayout>
                    <CustomerAuthStep
                        onCompleted={() =>
                            !hasMailingAddressStep
                                ? setWorkflowStage('application-with-co-application')
                                : setWorkflowStage('application')
                        }
                        onBack={() => setWorkflowStage('plan-selection')}
                    />
                </DefaultLayout>
            );
        case 'second-look-application':
            return (
                <DefaultLayout>
                    <SelectSecondLookStep
                        skipSelectProvider={!canChangeProvider}
                        onPrequalifySelected={() =>
                            setWorkflowStage('second-look-applicant-information')
                        }
                    />
                </DefaultLayout>
            );
        case 'application':
            return <ApplicationWorkflow />;
        case 'application-with-co-application':
            return <ApplicationWithCoApplicationWorkflow />;
        case 'second-look-applicant-information':
            return <SecondLookApplicationWorkflow useCustomButtons={canChangeProvider} />;
        case 'second-look-submitting':
            return (
                <DefaultLayout>
                    <ErrorBoundary
                        actionCausingError="submitting second look application"
                        onRetry={() => setWorkflowStage('second-look-applicant-information')}
                    >
                        <SecondLookSubmitLoanWorkflow />
                    </ErrorBoundary>
                </DefaultLayout>
            );
        case 'second-look-decision':
            return (
                <DefaultLayout>
                    <ErrorBoundary actionCausingError="loading qualification decision">
                        <SecondLookDecisionWorkflow />
                    </ErrorBoundary>
                </DefaultLayout>
            );
        case 'submitting':
            return (
                <DefaultLayout>
                    <ErrorBoundary
                        actionCausingError="submitting a loan"
                        onRetry={() =>
                            !hasMailingAddressStep
                                ? setWorkflowStage('application-with-co-application')
                                : setWorkflowStage('application')
                        }
                    >
                        <SubmitLoan />
                    </ErrorBoundary>
                </DefaultLayout>
            );
        case 'decision':
            return decisionData.decision === SubmitApplicationDecision.Declined ||
                decisionData.decision === SubmitApplicationDecision.Error ? (
                <FinalStepLayout>
                    <Declined />
                </FinalStepLayout>
            ) : decisionData.decision === SubmitApplicationDecision.Review ? (
                <FinalStepLayout>
                    <UnderReview />
                </FinalStepLayout>
            ) : (
                <DecisionWorkflow />
            );
        case 'accept-offer':
            return (
                <DefaultLayout>
                    <Approved />
                </DefaultLayout>
            );
        case 'update-offer':
            return (
                <DefaultLayout>
                    <UpdateOfferWorkflow />
                </DefaultLayout>
            );
        case 'reviewing': {
            const isWellsFargoProvider = setupData?.provider?.toLowerCase() === 'wellsfargo';
            return (
                <DefaultLayout>
                    <ErrorBoundary
                        actionCausingError={
                            isWellsFargoProvider
                                ? 'reviewing your pre-qualification'
                                : 'reviewing a loan'
                        }
                        onRetry={() => setWorkflowStage('application-with-co-application')}
                    >
                        {isWellsFargoProvider ? (
                            <WellsFargoReviewingWorkflow />
                        ) : (
                            <ReviewingWorkflow />
                        )}
                    </ErrorBoundary>
                </DefaultLayout>
            );
        }
        case 'signing':
            return signatureData.signedSuccessfully ? (
                <FinalStepLayout
                    doneButtonText={
                        setupData?.provider?.toLowerCase() === 'greensky'
                            ? 'View Estimates'
                            : 'Done'
                    }
                >
                    <LoanSignContainer />
                </FinalStepLayout>
            ) : (
                <DefaultLayout>
                    <LoanSignContainer />
                </DefaultLayout>
            );
        case 'reset':
            return <ResetWorkflow />;
        default:
            return null;
    }
};
